import React from 'react';

import { useAsyncDebounce } from 'react-table';
import {
  Field,
  Label,
  Suggestion,
  SuggestionItem,
  SuggestionInputProps,
  ValidationMessage,
} from '@innovationnorway/cl-react';
import { useTranslation } from 'react-i18next';
import '../../styles/style.scss';

export interface AutocompleteProps<T>
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  type?: SuggestionInputProps['type'];

  /**
   * Label for the input field
   */
  label?: string;

  /**
   * Array of items that the search will lookup into
   */
  items: Array<T>;

  /**
   * Render the value inside the input-field
   */
  itemLabel: (item: T) => string | number | readonly string[] | undefined;

  /**
   * How the filtering will be done
   */
  filter: ((item: T, input: string) => boolean) | false;

  /**
   * Render the values inside the select list
   */
  renderItem?: (item: T) => React.ReactNode;

  /**
   * Will trigger when an item in the list is selected.
   * Returns the whole object of
   */
  onItemSelected?: (item: T) => void;

  /**
   * Will trigger when a selected item is cleared or its input is edited.
   */
  onSelectionCleared?: () => void;

  /**
   * How long it will take from last onChange-event to suggestions will be shown, in milliseconds (Default: 250)
   */
  debounceTime?: number;

  /**
   * Reset input field after select. (Default: false)
   */
  resetAfterSelect?: boolean;

  /**
   * Sorting the filtered values
   */
  sort?: (a: T, b: T) => 1 | -1 | 0;

  /**
   * Max amount of items shown in dropdown
   */
  maxCount?: number;

  /**
   * Show error message
   */
  errorMsg?: string;

  inputClassName?: string;
}

const getDefaultSelection = <T,>(
  defaultValue: string | number | readonly string[] | undefined,
  items: T[],
  itemLabel: AutocompleteProps<T>['itemLabel'],
): SuggestionItem | null => {
  if (defaultValue == null) {
    return null;
  }

  const label = String(defaultValue);
  const matchingIndex = items.findIndex((item) => String(itemLabel(item) ?? '') === label);

  return { label, value: matchingIndex < 0 ? label : String(matchingIndex) };
};

function AutocompleteInner<T>(
  {
    items,
    itemLabel,
    filter,
    renderItem,
    onItemSelected,
    onSelectionCleared,
    debounceTime = 250,
    onChange,
    onInput,
    onFocus,
    onKeyDown,
    onPaste,
    defaultValue,
    value,
    resetAfterSelect = false,
    sort,
    maxCount = 25,
    errorMsg,
    className,
    inputClassName,
    label,
    id,
    required,
    ...rest
  }: AutocompleteProps<T>,
  ref?: React.ForwardedRef<HTMLInputElement>,
) {
  const { t: tCommon } = useTranslation();
  const generatedId = React.useId();
  const inputId = id ?? `autocomplete-${generatedId}`;
  const [searchValue, setSearchValue] = React.useState('');
  const [selected, setSelected] = React.useState<SuggestionItem | null>(() =>
    getDefaultSelection(defaultValue, items, itemLabel),
  );
  const selectedRef = React.useRef(selected);
  const previousDefaultValue = React.useRef(defaultValue);
  const selectionInputPending = React.useRef(false);
  const activeSelection =
    value === undefined ? selected : value === '' ? null : getDefaultSelection(value, items, itemLabel);

  React.useEffect(() => {
    if (previousDefaultValue.current !== defaultValue) {
      previousDefaultValue.current = defaultValue;
      const nextSelection = getDefaultSelection(defaultValue, items, itemLabel);
      selectedRef.current = nextSelection;
      setSelected(nextSelection);
    }
  }, [defaultValue, itemLabel, items]);

  React.useEffect(() => {
    if (value !== undefined) {
      selectedRef.current = activeSelection;
    }
  }, [activeSelection, value]);

  const setSearchValueDebounced = useAsyncDebounce(setSearchValue, debounceTime);
  const clearSelection = () => {
    const hadSelection = selectedRef.current !== null;
    selectedRef.current = null;
    setSelected(null);
    if (hadSelection) {
      onSelectionCleared?.();
    }
  };

  const options = React.useMemo(() => {
    let result = items.map((item, index) => ({
      item,
      option: { label: String(itemLabel(item) ?? ''), value: String(index) },
    }));

    if (searchValue && filter) {
      result = result.filter(({ item }) => filter(item, searchValue));
    }

    if (sort) {
      result.sort((a, b) => sort(a.item, b.item));
    }

    return result.slice(0, maxCount);
  }, [filter, itemLabel, items, maxCount, searchValue, sort]);

  const handleInput = (event: React.FormEvent<HTMLInputElement>) => {
    if (selectionInputPending.current) {
      selectionInputPending.current = false;
      return;
    }

    const inputValue = event.currentTarget.value;
    if (selectedRef.current?.label === inputValue) {
      return;
    }

    onInput?.(event);
    onChange?.(event as React.ChangeEvent<HTMLInputElement>);
    clearSelection();
    setSearchValueDebounced(inputValue);
  };

  const handleFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    onFocus?.(event);
    setSearchValue('');
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    selectionInputPending.current = false;
    onKeyDown?.(event);
  };

  const handlePaste = (event: React.ClipboardEvent<HTMLInputElement>) => {
    selectionInputPending.current = false;
    onPaste?.(event);
  };

  const handleSelectedChange = (value: SuggestionItem | null) => {
    selectionInputPending.current = value !== null;
    if (value === null) {
      clearSelection();
      setSearchValue('');
      return;
    }

    const nextSelection = resetAfterSelect ? null : value;
    selectedRef.current = nextSelection;
    setSelected(nextSelection);
    setSearchValue('');

    if (value) {
      const selectedItem = options.find(({ option }) => option.value === value.value)?.item;
      if (selectedItem !== undefined) {
        onItemSelected?.(selectedItem);
      }
    }
  };

  const resolvedRenderItem = renderItem ?? ((item: T) => <>{itemLabel(item)}</>);

  return (
    <div className={className}>
      <Field>
        {label && (
          <Label htmlFor={inputId}>
            {label}
            {required && <span aria-hidden="true">*</span>}
          </Label>
        )}
        <Suggestion selected={activeSelection} onSelectedChange={handleSelectedChange} filter={false}>
          <Suggestion.Input
            {...rest}
            ref={ref}
            id={inputId}
            required={required}
            autoComplete="off"
            className={inputClassName}
            aria-invalid={!!errorMsg}
            aria-errormessage={errorMsg ? `${inputId}-error` : undefined}
            onInput={handleInput}
            onFocus={handleFocus}
            onKeyDown={handleKeyDown}
            onPaste={handlePaste}
          />
          <Suggestion.Clear />
          <Suggestion.List>
            <Suggestion.Empty>{tCommon('noHits')}</Suggestion.Empty>
            {options.map(({ item, option }) => (
              <Suggestion.Option key={option.value} label={option.label} value={option.value}>
                {resolvedRenderItem(item)}
              </Suggestion.Option>
            ))}
          </Suggestion.List>
        </Suggestion>
        {errorMsg && (
          <ValidationMessage id={`${inputId}-error`} data-color="danger">
            {errorMsg}
          </ValidationMessage>
        )}
      </Field>
    </div>
  );
}

export const Autocomplete = React.forwardRef(AutocompleteInner) as <T>(
  props: AutocompleteProps<T> & { ref?: React.ForwardedRef<HTMLInputElement> },
) => ReturnType<typeof AutocompleteInner>;
